import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { detectSeriesFromTitle, groupBooksBySeries } from '@/lib/seriesUtils';
import { toast } from 'sonner';

export interface SeriesTracking {
  id: string;
  user_id: string;
  series_name: string;
  series_name_display: string;
  author_name: string;
  highest_book_position: number;
  total_books_known: number | null;
  is_dismissed: boolean;
  dismiss_reason: string | null;
  last_book_id: string | null;
  last_book_read_at: string | null;
  created_at: string;
  updated_at: string;
  books?: any[];
}

export function useSeriesTracking() {
  const queryClient = useQueryClient();

  // Fetch all series for current user
  const { data: allSeries = [], isLoading } = useQuery({
    queryKey: ['user-series-tracking'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_series_tracking')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data as SeriesTracking[];
    },
  });

  // Get active (non-dismissed) series
  const activeSeries = allSeries.filter(s => !s.is_dismissed);

  // Get dismissed series
  const dismissedSeries = allSeries.filter(s => s.is_dismissed);

  // Get completed series (read all available books)
  const completedSeries = activeSeries.filter(
    s => s.total_books_known && s.highest_book_position >= s.total_books_known
  );

  // Get in-progress series
  const inProgressSeries = activeSeries.filter(
    s => !s.total_books_known || s.highest_book_position < s.total_books_known
  );

  // Dismiss a series
  const dismissSeriesMutation = useMutation({
    mutationFn: async ({ seriesId, reason }: { seriesId: string; reason?: string }) => {
      const { error } = await supabase
        .from('user_series_tracking')
        .update({
          is_dismissed: true,
          dismiss_reason: reason || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', seriesId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-series-tracking'] });
      toast.success('Series dismissed');
    },
    onError: (error) => {
      console.error('Failed to dismiss series:', error);
      toast.error('Failed to dismiss series');
    },
  });

  // Reactivate a dismissed series
  const reactivateSeriesMutation = useMutation({
    mutationFn: async (seriesId: string) => {
      const { error } = await supabase
        .from('user_series_tracking')
        .update({
          is_dismissed: false,
          dismiss_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', seriesId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-series-tracking'] });
      toast.success('Series reactivated');
    },
    onError: (error) => {
      console.error('Failed to reactivate series:', error);
      toast.error('Failed to reactivate series');
    },
  });

  // Manually track a series (for client-side detected series)
  const trackSeriesMutation = useMutation({
    mutationFn: async ({
      seriesName,
      seriesNameDisplay,
      authorName,
      position,
      total,
      bookId,
      readAt,
    }: {
      seriesName: string;
      seriesNameDisplay: string;
      authorName: string;
      position: number;
      total?: number;
      bookId: string;
      readAt?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const normalizedSeries = seriesName.toLowerCase().trim();

      const { error } = await supabase
        .from('user_series_tracking')
        .upsert({
          user_id: user.id,
          series_name: normalizedSeries,
          series_name_display: seriesNameDisplay,
          author_name: authorName,
          highest_book_position: position,
          total_books_known: total || null,
          last_book_id: bookId,
          last_book_read_at: readAt || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,series_name,author_name',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-series-tracking'] });
    },
    onError: (error) => {
      console.error('Failed to track series:', error);
      toast.error('Failed to track series');
    },
  });

  // Get books in a specific series for the user
  const useSeriesBooks = (seriesName: string, authorName: string) => {
    return useQuery({
      queryKey: ['series-books', seriesName, authorName],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        // Get all books in this series that the user has on any shelf
        const { data, error } = await supabase
          .from('user_books')
          .select(`
            *,
            books (
              id,
              title,
              authors,
              cover_url,
              series_name,
              series_position,
              series_total
            )
          `)
          .eq('user_id', user.id)
          .not('books', 'is', null);

        if (error) throw error;

        // Filter to books in this series
        const seriesBooks = data.filter(ub => {
          const book = ub.books as any;
          if (!book) return false;
          
          const normalizedBookSeries = book.series_name?.toLowerCase().trim();
          const normalizedTargetSeries = seriesName.toLowerCase().trim();
          
          return normalizedBookSeries === normalizedTargetSeries &&
                 book.authors?.includes(authorName);
        });

        return seriesBooks;
      },
      enabled: !!seriesName && !!authorName,
    });
  };

  return {
    allSeries,
    activeSeries,
    dismissedSeries,
    completedSeries,
    inProgressSeries,
    isLoading,
    dismissSeries: dismissSeriesMutation.mutate,
    reactivateSeries: reactivateSeriesMutation.mutate,
    trackSeries: trackSeriesMutation.mutate,
    useSeriesBooks,
  };
}
